/** Forecasting & production accuracy tab - forecast vs actual demand, planned vs actual
 * sales, over/under-production, unsold prepared items, and recommended production
 * adjustments per site/menu item. */

import { Box, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Section } from '../../../components/ui/Section';
import { applyRoundAndFormat } from '../../../utils';
import { FORECAST_ACCURACY } from '../data';
import { StatusChip } from './StatusChip';

interface ForecastingTabProps {
  currencyCode?: string;
}

const ForecastingTab = ({ currencyCode }: ForecastingTabProps) => {
  const { t } = useTranslation('common');

  return (
    <Section>
      <Box sx={{ p: 4 }}>
        <Typography variant="textMD"
          fontWeight={600}
          sx={{ mb: 1 }}>
          {t('wasteDashboardForecastingTitle', 'Forecasting and production accuracy')}
        </Typography>
        <Typography variant="textSM"
          color="text.secondary"
          sx={{ mb: 3, display: 'block' }}>
          {t('wasteDashboardForecastingSubtitle', 'Forecast demand vs actual demand, planned production vs actual sales, and the waste this variance caused.')}
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('wasteDashboardSite', 'Site')}</TableCell>
              <TableCell>{t('wasteDashboardMenuItem', 'Menu item')}</TableCell>
              <TableCell align="right">{t('wasteDashboardForecastDemand', 'Forecast demand')}</TableCell>
              <TableCell align="right">{t('wasteDashboardActualDemand', 'Actual demand')}</TableCell>
              <TableCell align="right">{t('wasteDashboardPlannedProduction', 'Planned production')}</TableCell>
              <TableCell align="right">{t('wasteDashboardActualSales', 'Actual sales')}</TableCell>
              <TableCell align="right">{t('wasteDashboardOverproduction', 'Overproduction')}</TableCell>
              <TableCell align="right">{t('wasteDashboardUnderproduction', 'Underproduction')}</TableCell>
              <TableCell align="right">{t('wasteDashboardUnsoldPrepared', 'Unsold prepared')}</TableCell>
              <TableCell align="right">{t('wasteDashboardVarianceCost', 'Waste from variance')}</TableCell>
              <TableCell>{t('wasteDashboardRecommendedAdjustment', 'Recommended adjustment')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {FORECAST_ACCURACY.map((row) => (
              <TableRow key={`${row.site}-${row.menuItem}`}>
                <TableCell>{row.site}</TableCell>
                <TableCell>{row.menuItem}</TableCell>
                <TableCell align="right">{row.forecastDemandUnits}</TableCell>
                <TableCell align="right">{row.actualDemandUnits}</TableCell>
                <TableCell align="right">{row.plannedProductionUnits}</TableCell>
                <TableCell align="right">{row.actualSalesUnits}</TableCell>
                <TableCell align="right">
                  {row.overproductionUnits > 0 ? <StatusChip status="critical"
                    label={String(row.overproductionUnits)} /> : '0'}
                </TableCell>
                <TableCell align="right">
                  {row.underproductionUnits > 0 ? <StatusChip status="warning"
                    label={String(row.underproductionUnits)} /> : '0'}
                </TableCell>
                <TableCell align="right">{row.unsoldPreparedUnits}</TableCell>
                <TableCell align="right">{applyRoundAndFormat(row.wasteFromForecastVarianceCost, currencyCode)}</TableCell>
                <TableCell>{row.recommendedProductionAdjustment}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </Section>
  );
};

export default ForecastingTab;
